import { PubMedItem } from '../../view/search-list/types';
import { LanguageService } from '../language';
import { buildEndpointMap } from './build-endpoint-map';
import { RocheEndpoint, RochePipelineEndpoint, rootDomain, storyblokRootDomain } from './types';
import { LocalSafetyItem } from './types/local-safety-officers';
import { Location } from './types/locations';
import { EventItem } from './types/media-events';
import { MediaItem } from './types/media-news';
import {
  EventsSearchParams,
  LocationsSearchParams,
  NewsSearchParams,
  NewsYearsSearchParams,
  StockInformationSearchParams,
  AdhocSearchParams,
  EventYearSearchParams,
  PubMedSearchParams,
  PatientSearchParams,
  GetCountryLocationSearchParams,
} from './types/params';
import { Pipeline, PipelineChanges, PipelineDiagnostics } from './types/pipeline';
import { StockInformation } from './types/roche-stock';

export class ExternalDataService {
  private static endpoints = buildEndpointMap(rootDomain!, storyblokRootDomain!);

  // eslint-disable-next-line no-undef
  static async get<T>(endpoint: RequestInfo, params?: RequestInit): Promise<T> {
    const apiResponse = await fetch(endpoint, params)
      .then((response) => response.json() as Promise<T>)
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error);
        throw error; // Re-throw the error after logging it
      });
    return apiResponse;
  }

  static buildSearchParams<T>(params: T): string {
    return Object.entries(params as Record<string, unknown>)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
      .join('&');
  }

  static async getEventYears(params: EventYearSearchParams): Promise<string[]> {
    return this.get<string[]>(`${this.endpoints.endpoint.eventYears}?${this.buildSearchParams(params)}`);
  }

  static getLocalisedValue(object: Record<string, unknown>, key: string, locale: string): string {
    return object[key]?.[locale] || object[key]?.[LanguageService.getDefaultLocale()];
  }

  static async getEvents(params: EventsSearchParams): Promise<RocheEndpoint<MediaItem[]>> {
    return this.get<RocheEndpoint<MediaItem[]>>(`${this.endpoints.endpoint.events}?${this.buildSearchParams(params)}`);
  }

  static async getUpcomingEvents(params: EventsSearchParams): Promise<RocheEndpoint<EventItem[]>> {
    return this.get<RocheEndpoint<EventItem[]>>(
      `${this.endpoints.endpoint.upcomingEvents}?${this.buildSearchParams(params)}`,
    );
  }

  static async getLocationsByContinent(params: LocationsSearchParams): Promise<RocheEndpoint<Location>> {
    return this.get<RocheEndpoint<Location>>(`${this.endpoints.endpoint.locations}?${this.buildSearchParams(params)}`);
  }

  static async getNewsYears(params: NewsYearsSearchParams): Promise<string[]> {
    return this.get<string[]>(`${this.endpoints.endpoint.newsYears}?${this.buildSearchParams(params)}`);
  }

  static async getNews(params: NewsSearchParams): Promise<RocheEndpoint<MediaItem[]>> {
    return this.get<RocheEndpoint<MediaItem[]>>(`${this.endpoints.endpoint.news}?${this.buildSearchParams(params)}`);
  }

  static async getAdhoc(params?: AdhocSearchParams): Promise<RocheEndpoint<MediaItem[]>> {
    return this.get<RocheEndpoint<MediaItem[]>>(
      `${this.endpoints.endpoint.adhocAnnouncement}?${this.buildSearchParams(params)}`,
    );
  }

  static async getAdhocYears(): Promise<string[]> {
    return this.get<string[]>(`${this.endpoints.endpoint.adhocAnnouncementYears}`);
  }

  static async getSafetyReporting(): Promise<unknown> {
    return this.get(this.endpoints.endpoint.safetyReporting);
  }

  static async getSafetyOfficers(): Promise<RocheEndpoint<LocalSafetyItem>> {
    return this.get<RocheEndpoint<LocalSafetyItem>>(this.endpoints.endpoint.safetyOfficers);
  }

  static async getStockInformation(params: StockInformationSearchParams): Promise<StockInformation> {
    return this.get<StockInformation>(`${this.endpoints.endpoint.stockInformation}?${this.buildSearchParams(params)}`);
  }

  static async getLocation(params: GetCountryLocationSearchParams): Promise<RocheEndpoint<Location>> {
    return this.get<RocheEndpoint<Location>>(`${this.endpoints.endpoint.locations}?${this.buildSearchParams(params)}`);
  }

  static async getAllCountryLocationsByContinent(params: LocationsSearchParams): Promise<RocheEndpoint<Location[]>> {
    return this.get<RocheEndpoint<Location[]>>(
      `${this.endpoints.endpoint.locationsByContinent}?${this.buildSearchParams(params)}`,
    );
  }

  static async getPipeline(): Promise<RochePipelineEndpoint<Pipeline>> {
    return this.get<RochePipelineEndpoint<Pipeline>>(this.endpoints.endpoint.pipeline);
  }

  static async getPipelineDiagnostics(): Promise<RochePipelineEndpoint<PipelineDiagnostics>> {
    return this.get<RochePipelineEndpoint<PipelineDiagnostics>>(this.endpoints.endpoint.pipelineDiagnostics);
  }

  static async getPipelineChanges(): Promise<RochePipelineEndpoint<PipelineChanges>> {
    return this.get<RochePipelineEndpoint<PipelineChanges>>(this.endpoints.endpoint.pipelineChanges);
  }

  static async getPubMed(params: PubMedSearchParams, searchQuery: string, category = ''): Promise<PubMedItem> {
    return this.get(
      `${this.endpoints.endpoint.pubMed}?${this.buildSearchParams(params)}&query=${encodeURIComponent(
        searchQuery,
      )}&function=${encodeURIComponent(category)}`,
    );
  }

  static async getPubMedCategories(): Promise<{ functions: string[] }> {
    return this.get<{ functions: string[] }>(this.endpoints.endpoint.pubMedCategories);
  }

  static async getPatients(params: PatientSearchParams): Promise<unknown> {
    return this.get(`${this.endpoints.endpoint.patient}?${this.buildSearchParams(params)}`);
  }

  static async handle404(response: any): Promise<void> {
    // TODO: Update API and use response status code for handling 404
    if (response.message || response.error) {
      window.location.replace('/404');
    }
  }

  static getContentStyleOverwrite(): string {
    return `
      <style>
        #gnw_attachments_section-header,#gnw_attachments_section-items{display:none;}
      </style>
      `;
  }
}
